"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.YearnVaultApi = void 0;
const providers_1 = require("@ethersproject/providers");
const sdk_1 = require("@yfi/sdk");
const lodash_1 = require("lodash");
const isNil_1 = __importDefault(require("lodash/isNil"));
const web3_1 = __importDefault(require("web3"));
const constants_1 = require("../constants");
const utils_1 = require("../utils");
class YearnVaultApi {
    constructor({ adapter, providerUrl, network = 1 }) {
        this.adapter = adapter;
        this.provider = new web3_1.default.providers.HttpProvider(providerUrl);
        this.jsonRpcProvider = new providers_1.JsonRpcProvider(providerUrl);
        this.web3 = new web3_1.default(this.provider);
        this.yearnSdk = new sdk_1.Yearn(network, { provider: this.jsonRpcProvider });
        this.ssRouterContract = new this.web3.eth.Contract(constants_1.ssRouterAbi, constants_1.ssRouterContractAddress);
        this.vaults = [];
    }
    async initialize() {
        await this.yearnSdk.ready;
        this.vaults = await this.findAll();
    }
    async findAll() {
        return this.yearnSdk.vaults.get();
    }
    findByDepositVaultAddress(vaultAddress) {
        const vault = this.vaults.find((item) => (0, lodash_1.toLower)(item.address) === (0, lodash_1.toLower)(vaultAddress));
        if (!vault)
            return null;
        return (0, utils_1.transformVault)(vault);
    }
    findByVaultTokenId(vaultAddress) {
        const vault = this.vaults.find((item) => (0, lodash_1.toLower)(item.address) === (0, lodash_1.toLower)(vaultAddress));
        if (!vault)
            return null;
        return (0, utils_1.transformVault)(vault);
    }
    async getGasPrice() {
        const gasPrice = await this.web3.eth.getGasPrice();
        return (0, utils_1.bnOrZero)(gasPrice);
    }
    async getTxReceipt({ txid }) {
        return await this.web3.eth.getTransactionReceipt(txid);
    }
    checksumAddress(address) {
        return this.web3.utils.toChecksumAddress(address);
    }
    // From the token contract address and vault address, we need to get the vault id. The router
    // contract needs the vault id to know which vault it is dealing with when depositing, since it
    // takes a token address and a vault id.
    async getVaultId({ tokenContractAddress, vaultAddress }) {
        const numVaults = await this.ssRouterContract.methods
            .numVaults(this.checksumAddress(tokenContractAddress))
            .call();
        let id = null;
        for (let i = 0; i <= numVaults && (0, isNil_1.default)(id); i++) {
            const result = await this.ssRouterContract.methods
                .vaults(this.checksumAddress(tokenContractAddress), i)
                .call();
            if (result === this.checksumAddress(vaultAddress))
                id = i;
        }
        if ((0, isNil_1.default)(id))
            throw new Error(`Could not find vault id for token: ${tokenContractAddress} vault: ${vaultAddress}`);
        return id;
    }
    async estimateApproveGas(input) {
        const { userAddress, tokenContractAddress } = input;
        const depositTokenContract = new this.web3.eth.Contract(constants_1.erc20Abi, tokenContractAddress);
        const estimatedGas = await depositTokenContract.methods
            .approve(constants_1.ssRouterContractAddress, constants_1.MAX_ALLOWANCE)
            .estimateGas({
            from: userAddress
        });
        return (0, utils_1.bnOrZero)(estimatedGas);
    }
    async approve(input) {
        const { accountNumber = 0, dryRun = false, tokenContractAddress, userAddress, wallet } = input;
        if (!wallet)
            throw new Error('Missing inputs');
        const estimatedGas = await this.estimateApproveGas(input);
        const depositTokenContract = new this.web3.eth.Contract(constants_1.erc20Abi, tokenContractAddress);
        const data = depositTokenContract.methods
            .approve(constants_1.ssRouterContractAddress, constants_1.MAX_ALLOWANCE)
            .encodeABI({
            from: userAddress
        });
        const nonce = await this.web3.eth.getTransactionCount(userAddress);
        const gasPrice = await this.web3.eth.getGasPrice();
        const txToSign = (0, utils_1.buildTxToSign)({
            bip44Params: this.adapter.buildBIP44Params({ accountNumber }),
            chainId: 1,
            data,
            estimatedGas: estimatedGas.toString(),
            gasPrice,
            nonce: String(nonce),
            to: tokenContractAddress,
            value: '0'
        });
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await this.adapter.signTransaction({ txToSign, wallet });
            if (dryRun)
                return signedTx;
            return this.adapter.broadcastTransaction(signedTx);
        }
        else if (wallet.supportsBroadcast() && this.adapter.signAndBroadcastTransaction) {
            if (dryRun) {
                throw new Error(`Cannot perform a dry run with wallet of type ${wallet.getVendor()}`);
            }
            return this.adapter.signAndBroadcastTransaction({ txToSign, wallet });
        }
        else {
            throw new Error('Invalid HDWallet configuration ');
        }
    }
    async allowance(input) {
        const { userAddress, tokenContractAddress } = input;
        const depositTokenContract = new this.web3.eth.Contract(constants_1.erc20Abi, tokenContractAddress);
        return depositTokenContract.methods.allowance(userAddress, constants_1.ssRouterContractAddress).call();
    }
    async estimateDepositGas(input) {
        const { amountDesired, userAddress, tokenContractAddress, vaultAddress } = input;
        const vaultIndex = await this.getVaultId({ tokenContractAddress, vaultAddress });
        const tokenChecksum = this.web3.utils.toChecksumAddress(tokenContractAddress);
        const userChecksum = this.web3.utils.toChecksumAddress(userAddress);
        const estimatedGas = await this.ssRouterContract.methods
            .deposit(tokenChecksum, userChecksum, amountDesired.toString(), vaultIndex)
            .estimateGas({
            value: 0,
            from: userChecksum
        });
        return (0, utils_1.bnOrZero)(estimatedGas);
    }
    async deposit(input) {
        const { amountDesired, accountNumber = 0, dryRun = false, tokenContractAddress, vaultAddress, userAddress, wallet } = input;
        if (!wallet || !vaultAddress)
            throw new Error('Missing inputs');
        const estimatedGas = await this.estimateDepositGas(input);
        // In order to properly earn affiliate revenue, we must deposit to the vault through the SS
        // router contract. This is not necessary for withdraws. We can withdraw directly from the vault
        // without affecting the DAOs affiliate revenue.
        const tokenChecksum = this.web3.utils.toChecksumAddress(tokenContractAddress);
        const userChecksum = this.web3.utils.toChecksumAddress(userAddress);
        const vaultIndex = await this.getVaultId({ tokenContractAddress, vaultAddress });
        const data = await this.ssRouterContract.methods
            .deposit(tokenChecksum, userChecksum, amountDesired.toString(), vaultIndex)
            .encodeABI({
            value: 0,
            from: userChecksum
        });
        const nonce = await this.web3.eth.getTransactionCount(userAddress);
        const gasPrice = await this.web3.eth.getGasPrice();
        const txToSign = (0, utils_1.buildTxToSign)({
            bip44Params: this.adapter.buildBIP44Params({ accountNumber }),
            chainId: 1,
            data,
            estimatedGas: estimatedGas.toString(),
            gasPrice,
            nonce: String(nonce),
            to: constants_1.ssRouterContractAddress,
            value: '0'
        });
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await this.adapter.signTransaction({ txToSign, wallet });
            if (dryRun)
                return signedTx;
            return this.adapter.broadcastTransaction(signedTx);
        }
        else if (wallet.supportsBroadcast() && this.adapter.signAndBroadcastTransaction) {
            if (dryRun) {
                throw new Error(`Cannot perform a dry run with wallet of type ${wallet.getVendor()}`);
            }
            return this.adapter.signAndBroadcastTransaction({ txToSign, wallet });
        }
        else {
            throw new Error('Invalid HDWallet configuration ');
        }
    }
    // Withdraws are done through the vault contract itself, there is no need to go through the SS
    // router contract, so we estimate the gas from the vault itself.
    async estimateWithdrawGas(input) {
        const { amountDesired, userAddress, vaultAddress } = input;
        const vaultContract = new this.web3.eth.Contract(constants_1.yv2VaultAbi, vaultAddress);
        const estimatedGas = await vaultContract.methods
            .withdraw(amountDesired.toString(), userAddress)
            .estimateGas({
            from: userAddress
        });
        return (0, utils_1.bnOrZero)(estimatedGas);
    }
    async withdraw(input) {
        const { amountDesired, accountNumber = 0, dryRun = false, vaultAddress, userAddress, wallet } = input;
        if (!wallet || !vaultAddress)
            throw new Error('Missing inputs');
        const estimatedGas = await this.estimateWithdrawGas(input);
        // We use the vault directly to withdraw the vault tokens. There is no benefit to the DAO to use
        // the router to withdraw funds and there is an extra approval required for the user if we
        // withdrew from the vault using the shapeshift router. Affiliate fees for SS are the same
        // either way. For this reason, we simply withdraw from the vault directly.
        const vaultContract = new this.web3.eth.Contract(constants_1.yv2VaultAbi, vaultAddress);
        const data = vaultContract.methods
            .withdraw(amountDesired.toString(), userAddress)
            .encodeABI({
            from: userAddress
        });
        const nonce = await this.web3.eth.getTransactionCount(userAddress);
        const gasPrice = await this.web3.eth.getGasPrice();
        const txToSign = (0, utils_1.buildTxToSign)({
            bip44Params: this.adapter.buildBIP44Params({ accountNumber }),
            chainId: 1,
            data,
            estimatedGas: estimatedGas.toString(),
            gasPrice,
            nonce: String(nonce),
            to: vaultAddress,
            value: '0'
        });
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await this.adapter.signTransaction({ txToSign, wallet });
            if (dryRun)
                return signedTx;
            return this.adapter.broadcastTransaction(signedTx);
        }
        else if (wallet.supportsBroadcast() && this.adapter.signAndBroadcastTransaction) {
            if (dryRun) {
                throw new Error(`Cannot perform a dry run with wallet of type ${wallet.getVendor()}`);
            }
            return this.adapter.signAndBroadcastTransaction({ txToSign, wallet });
        }
        else {
            throw new Error('Invalid HDWallet configuration ');
        }
    }
    async balance(input) {
        const { vaultAddress, userAddress } = input;
        const contract = new this.web3.eth.Contract(constants_1.yv2VaultAbi, vaultAddress);
        const balance = await contract.methods.balanceOf(userAddress).call();
        return (0, utils_1.bnOrZero)(balance);
    }
    async token(input) {
        const { vaultAddress } = input;
        const contract = new this.web3.eth.Contract(constants_1.yv2VaultAbi, vaultAddress);
        const address = await contract.methods.token().call();
        return address;
    }
    async totalSupply({ contractAddress }) {
        const contract = new this.web3.eth.Contract(constants_1.erc20Abi, contractAddress);
        const totalSupply = await contract.methods.totalSupply().call();
        return (0, utils_1.bnOrZero)(totalSupply);
    }
    async pricePerShare(input) {
        const contract = new this.web3.eth.Contract(constants_1.yv2VaultAbi, input.vaultAddress);
        const pricePerShare = await contract.methods.pricePerShare().call();
        return (0, utils_1.bnOrZero)(pricePerShare);
    }
    async apy(input) {
        var _a, _b, _c, _d;
        const vaults = await this.findAll();
        const vaultData = vaults.find((vault) => vault.address === input.vaultAddress);
        if (!((_b = (_a = vaultData === null || vaultData === void 0 ? void 0 : vaultData.metadata) === null || _a === void 0 ? void 0 : _a.apy) === null || _b === void 0 ? void 0 : _b.net_apy)) {
            throw new Error('Not Found');
        }
        return String((_d = (_c = vaultData === null || vaultData === void 0 ? void 0 : vaultData.metadata) === null || _c === void 0 ? void 0 : _c.apy) === null || _d === void 0 ? void 0 : _d.net_apy);
    }
}
exports.YearnVaultApi = YearnVaultApi;
